import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PageHero } from "@/components/shared/PageHero";
import { Reveal, Stagger, StaggerItem } from "@/components/shared/Reveal";
import { SectionHeading } from "@/components/shared/SectionHeading";
import { CTABand } from "@/components/shared/CTABand";
import { COMPANY } from "@/data/company";
import { INDUSTRIES } from "@/data/industries";
import { useSEO, ORG_JSONLD } from "@/lib/seo";

const CAPABILITIES = [
  { title: "Material Selection", text: "Cores and covers specified against the duty — not off a shelf." },
  { title: "Compound Development", text: "Elastomer compounds formulated and mixed in-house." },
  { title: "Machining & Welding", text: "Precision machining and certified welding across diverse alloys." },
  { title: "Covering", text: "Rubber and Ribbon Build System polyurethane covering." },
  { title: "Testing", text: "Every roll inspected and tested against its specification." },
  { title: "Precision Finishing", text: "Grinding and finishing to the required geometry and surface." },
];

export default function About() {
  useSEO({
    title: "About Us — Five Decades of Roller Expertise",
    description:
      "Vikas Rolls Private Limited has manufactured industrial rolls since 1970 from its facility in Bahadurgarh, Haryana — controlling material selection, compounding, machining, covering and finishing under one roof.",
    jsonLd: ORG_JSONLD,
  });

  return (
    <>
      <PageHero
        chapter="A"
        label="Company"
        title={<>More Than Five Decades of<br className="hidden md:block" /> Industrial Roller Expertise</>}
        sub="Vikas Rolls began in 1970 and has grown into one of India's most trusted names in rubber covered rolls and engineered industrial roll products."
        crumbs={[{ label: "About Us" }]}
      />

      <section className="bg-ink py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-[1.2fr_1fr] gap-14 items-start">
          <div>
            <SectionHeading chapter="01" label="Our Story" title="From a modest start to a manufacturing standard." className="mb-8" />
            <Reveal delay={0.1}>
              <div className="space-y-5 text-steel text-base md:text-lg leading-relaxed">
                <p>
                  Vikas Rolls Private Limited started in 1970 and has steadily built its reputation as
                  one of India's preferred manufacturers of rubber covered rolls and industrial roll
                  products. The foundation of that reputation is simple: consistent product quality
                  and dependable service, project after project.
                </p>
                <p>
                  The company manufactures industrial rolls across an unusually wide size range — from
                  small precision components to large industrial rolls — serving varied applications
                  across steel, paper, packaging, textile, printing and many other industries.
                </p>
                <p>
                  Skilled people and capable machinery work together at every stage. As industries and
                  customer requirements evolve, Vikas Rolls continues to develop new products and
                  covering solutions to match.
                </p>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.2}>
            <div className="border border-line bg-ink-2 p-7">
              <p className="font-mono-tech text-[11px] uppercase tracking-[0.25em] text-amber-brand mb-3">Established</p>
              <p className="font-display font-black text-6xl text-steel-light leading-none">1970</p>
              <div className="h-px bg-line my-6" />
              <p className="font-mono-tech text-[11px] uppercase tracking-[0.25em] text-amber-brand mb-3">Manufacturing Facility</p>
              <p className="text-steel text-sm leading-relaxed">Bahadurgarh, Haryana, India — where products are developed, manufactured, tested and dispatched.</p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-ink-2 border-y border-line py-20 md:py-28 bg-blueprint">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <SectionHeading chapter="02" label="Capabilities" title={<>End-to-end process control,<br />under one roof.</>} className="mb-6" />
          <Reveal delay={0.1}>
            <p className="max-w-2xl text-steel text-base md:text-lg leading-relaxed mb-14">
              Vikas Rolls manages the complete process — material selection, compound development,
              mixing, manufacturing, machining, covering, testing and precision finishing. One
              supplier, accountable for the whole roll.
            </p>
          </Reveal>
          <Stagger className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-line border border-line">
            {CAPABILITIES.map((c, i) => (
              <StaggerItem key={c.title} className="bg-ink-2">
                <div className="p-7 md:p-8 h-full hover:bg-ink-3 transition-colors duration-300">
                  <span className="font-mono-tech text-[10px] text-amber-brand">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="font-display font-bold text-xl text-steel-light mt-3 mb-2">{c.title}</h3>
                  <p className="text-sm text-steel leading-relaxed">{c.text}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="bg-ink py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-2 gap-14 items-start">
          <div>
            <SectionHeading chapter="03" label="Markets Served" title="Varied industries. One standard of work." className="mb-8" />
            <Reveal delay={0.1}>
              <div className="flex flex-wrap gap-2.5">
                {INDUSTRIES.map((ind) => (
                  <Link
                    key={ind.slug}
                    to={`/industries/${ind.slug}`}
                    data-testid={`about-industry-chip-${ind.slug}`}
                    className="border border-line px-4 py-2 text-sm text-steel hover:border-amber-brand hover:text-amber-brand"
                  >
                    {ind.name}
                  </Link>
                ))}
              </div>
            </Reveal>
          </div>
          <div>
            <SectionHeading chapter="04" label="Leadership" title="Guided by experience." className="mb-8" />
            <Reveal delay={0.1}>
              <div className="border border-line bg-ink-2 p-7 md:p-8">
                <p className="font-display font-bold text-2xl text-steel-light">{COMPANY.director}</p>
                <p className="font-mono-tech text-[11px] uppercase tracking-[0.25em] text-amber-brand mt-2 mb-4">Director</p>
                <p className="text-steel text-sm md:text-base leading-relaxed">
                  Under the direction of {COMPANY.director}, Vikas Rolls continues to advance — building
                  long-term relationships with customers on a foundation of quality products and
                  dependable service.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-ink pb-20 md:pb-28">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <Reveal>
            <div className="border border-line bg-ink-2 p-8 md:p-12 flex flex-col md:flex-row md:items-center justify-between gap-8">
              <div>
                <h2 className="font-display font-black tracking-tight text-2xl md:text-4xl text-steel-light">Have an application to discuss?</h2>
                <p className="text-steel mt-3 max-w-xl">Tell us about your process and operating conditions — we'll recommend the right construction, cover and finish.</p>
              </div>
              <Link to="/request-a-quote" data-testid="about-cta-button" className="group flex-none inline-flex items-center gap-3 bg-amber-brand text-ink font-display font-bold px-8 py-4 hover:bg-steel-light">
                Discuss Your Application
                <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <CTABand />
    </>
  );
}
